import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { type ReactElement, useMemo, useState } from 'react';

import SetIcon from '@/components/SetIcon/SetIcon';
import { setsStateAtom } from '@/features/sets/state/setsAtom';
import type { ScryfallSet } from '@/types/card';

import { useDraftActions } from '../hooks/useDraftActions';
import { DEFAULT_CARDS_PER_PICK, DEFAULT_POD_SIZE, DRAFTABLE_SET_TYPES } from '../types';

export default function DraftSetup(): ReactElement {
  const setsState = useAtomValue(setsStateAtom);
  const { startDraft } = useDraftActions();

  const [selectedSet, setSelectedSet] = useState<ScryfallSet | null>(null);
  const [opponents, setOpponents] = useState(DEFAULT_POD_SIZE - 1);
  const [cardsPerPick, setCardsPerPick] = useState(DEFAULT_CARDS_PER_PICK);
  const [searchQuery, setSearchQuery] = useState('');

  const draftableSets = useMemo(() => {
    if (!setsState.data) return [];
    return setsState.data
      .filter((s) => DRAFTABLE_SET_TYPES.includes(s.set_type as (typeof DRAFTABLE_SET_TYPES)[number]) && !s.digital && s.card_count > 50)
      .sort((a, b) => {
        const dateA = a.released_at ?? '';
        const dateB = b.released_at ?? '';
        return dateB.localeCompare(dateA);
      });
  }, [setsState.data]);

  const filteredSets = useMemo(() => {
    if (!searchQuery.trim()) return draftableSets;
    const q = searchQuery.toLowerCase();
    return draftableSets.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
  }, [draftableSets, searchQuery]);

  const handleStart = () => {
    if (!selectedSet) return;
    startDraft(selectedSet.code, selectedSet.name, opponents + 1, cardsPerPick);
  };

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col items-center justify-center gap-8 px-4 py-12">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-zinc-100">Draft Simulator</h1>
        <p className="mt-2 text-sm text-zinc-400">Practice drafting against AI opponents</p>
      </div>

      {/* Set Selection */}
      <div className="w-full space-y-3">
        <label className="block text-sm font-medium text-zinc-300">Select a Set</label>
        <input
          type="text"
          placeholder="Search sets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        <div className="max-h-64 overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-800/30">
          {filteredSets.length === 0 && <p className="px-4 py-8 text-center text-sm text-zinc-500">No sets found</p>}
          {filteredSets.map((set) => (
            <button
              key={set.id}
              onClick={() => setSelectedSet(set)}
              className={clsx(
                'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                selectedSet?.id === set.id ? 'bg-blue-600/20 text-blue-300' : 'text-zinc-300 hover:bg-zinc-700/50',
              )}
            >
              <SetIcon setCode={set.code} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{set.name}</p>
                <p className="text-xs text-zinc-500">
                  {set.code.toUpperCase()} · {set.card_count} cards
                  {set.released_at && ` · ${set.released_at.slice(0, 4)}`}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected set preview */}
      {selectedSet && (
        <div className="flex w-full items-center gap-3 rounded-lg border border-blue-500/30 bg-blue-950/20 px-4 py-3">
          <SetIcon setCode={selectedSet.code} size="md" />
          <div>
            <p className="font-medium text-zinc-100">{selectedSet.name}</p>
            <p className="text-xs text-zinc-400">
              {selectedSet.code.toUpperCase()} · {selectedSet.card_count} cards · {selectedSet.set_type}
            </p>
          </div>
        </div>
      )}

      {/* Options */}
      <div className="flex w-full gap-6">
        {/* Opponents */}
        <div className="flex-1 space-y-2">
          <label className="block text-sm font-medium text-zinc-300">Opponents ({opponents})</label>
          <input
            type="range"
            min={1}
            max={7}
            value={opponents}
            onChange={(e) => setOpponents(Number(e.target.value))}
            className="w-full accent-blue-500"
          />
          <p className="text-xs text-zinc-500">{opponents + 1}-player pod</p>
        </div>

        {/* Cards per pick */}
        <div className="flex-1 space-y-2">
          <label className="block text-sm font-medium text-zinc-300">Cards per Pick</label>
          <div className="flex gap-2">
            <button
              onClick={() => setCardsPerPick(1)}
              className={clsx(
                'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                cardsPerPick === 1 ? 'bg-blue-600 text-white' : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700',
              )}
            >
              1 Card
            </button>
            <button
              onClick={() => setCardsPerPick(2)}
              className={clsx(
                'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                cardsPerPick === 2 ? 'bg-blue-600 text-white' : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700',
              )}
            >
              2 Cards
            </button>
          </div>
        </div>
      </div>

      {/* Start button */}
      <button
        onClick={handleStart}
        disabled={!selectedSet}
        className="w-full rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Start Draft
      </button>
    </div>
  );
}
